/**
 * Shared MCP tier configuration.
 *
 * Single source of truth for tier pricing, quotas, and overage rates.
 * Consumed by both the subscription-modal (new subscriptions) and
 * edit-subscription-modal (plan changes).
 *
 * Feature: 008-mcp-distribution-billing
 * Feature: 009-unified-billing-experience
 */

export type SupportedCurrency = 'USD' | 'AUD';

export const MCP_TIERS = ['free', 'lite', 'pro', 'max'] as const;
export type McpTier = (typeof MCP_TIERS)[number];

/** Monthly prices. Approved USD snapshot: AUD × 0.7126 (RBA, 18 Sep 2026), rounded to whole dollars. */
export const MCP_TIER_PRICES: Record<SupportedCurrency, Record<McpTier, number>> = {
  USD: { free: 0, lite: 14, pro: 36, max: 107 },
  AUD: { free: 0, lite: 20, pro: 50, max: 150 },
};

/** Monthly action quota per tier. */
export const MCP_TIER_QUOTAS: Record<McpTier, number> = {
  free: 500,
  lite: 2_000,
  pro: 6_000,
  max: 20_000,
};

/** USD overage: AUD × 0.7126, rounded to five decimal places per call. */
export const MCP_OVERAGE_RATES: Record<McpTier, number> = {
  free: 0,
  lite: 0.01069,
  pro: 0.00891,
  max: 0.00802,
};

/** Per-action overage rates in each supported billing currency. */
export const MCP_OVERAGE_RATES_BY_CURRENCY: Record<
  SupportedCurrency,
  Record<McpTier, number>
> = {
  USD: MCP_OVERAGE_RATES,
  AUD: { free: 0, lite: 0.015, pro: 0.0125, max: 0.01125 },
};

/** Public-facing label for the internal `pro` tier. */
export function getMcpTierLabel(tier: McpTier): string {
  return tier === 'pro' ? 'Standard' : tier[0]!.toUpperCase() + tier.slice(1);
}

export function getMcpOverageRate(
  tier: McpTier,
  currency: SupportedCurrency = 'USD',
): number {
  return MCP_OVERAGE_RATES_BY_CURRENCY[currency][tier];
}

export function formatMcpOverageRate(
  tier: McpTier,
  currency: SupportedCurrency = 'USD',
): string {
  return getMcpOverageRate(tier, currency)
    .toFixed(5)
    .replace(/0+$/, '')
    .replace(/\.$/, '');
}
